/**
 * The booking window each screen shows, so a poll asks for that and nothing
 * more (capacity review, item 4). Every window starts at a local midnight, so
 * it is the same string all day and the query key never churns between polls.
 */

export type BookingWindow = { from: string; to: string };

const DAY_MS = 86_400_000;

/** Mirrors the API's cap on one bookings read; a wider ask would be clamped. */
export const BOOKINGS_MAX_SPAN_DAYS = 62;
/** How far back an unclosed visit stays in the Agenda's Por cerrar. */
export const AGENDA_LOOKBACK_DAYS = 31;
/** How far ahead the Agenda lists upcoming visits. */
export const AGENDA_LOOKAHEAD_DAYS = 31;
/** How far back an unpaid visit stays on the counter's list. */
export const CHARGE_LOOKBACK_DAYS = 7;
/** The Clases roster shows the next seven days with classes; two weeks holds them. */
export const ROSTER_LOOKAHEAD_DAYS = 14;
/** A coach's week: the classes just taught and the ones coming up. */
export const COACH_LOOKBACK_DAYS = 7;
export const COACH_LOOKAHEAD_DAYS = 8;

/** Local midnight of the day `date` falls in, as epoch milliseconds. */
function midnightOf(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function around(date: Date, daysBack: number, daysAhead: number): BookingWindow {
  const midnight = midnightOf(date);
  return {
    from: new Date(midnight - daysBack * DAY_MS).toISOString(),
    to: new Date(midnight + daysAhead * DAY_MS).toISOString(),
  };
}

/** Reservas: Por cerrar behind today, hoy, and the month ahead. Pagos links from the same list. */
export function agendaWindow(now: Date): BookingWindow {
  return around(now, AGENDA_LOOKBACK_DAYS, AGENDA_LOOKAHEAD_DAYS);
}

/** Calendario: exactly the calendar day on screen. */
export function dayWindow(day: Date): BookingWindow {
  return around(day, 0, 1);
}

/** Cobrar: the visits still to charge, from last week through the end of today. */
export function chargeWindow(now: Date): BookingWindow {
  return around(now, CHARGE_LOOKBACK_DAYS, 1);
}

/** Clases: the roster from today forward. */
export function rosterWindow(now: Date): BookingWindow {
  return around(now, 0, ROSTER_LOOKAHEAD_DAYS);
}

/** Mis clases: the coach's week, for both the sessions and their bookings. */
export function coachWeekWindow(now: Date): BookingWindow {
  return around(now, COACH_LOOKBACK_DAYS, COACH_LOOKAHEAD_DAYS);
}

/** Equipo's vacation check: upcoming visits, as far ahead as one read reaches. */
export function upcomingWindow(now: Date): BookingWindow {
  return around(now, 0, BOOKINGS_MAX_SPAN_DAYS);
}

/** Every window lives under one prefix, so invalidating `['business-bookings']` reaches them all. */
export function bookingsQueryKey(window: BookingWindow): readonly ['business-bookings', string, string] {
  return ['business-bookings', window.from, window.to];
}
